<template>
  <div class="bento_canvas">
    <!-- Ambient Specular Glows -->
    <div class="ambient_sphere sphere_purple"></div>
    <div class="ambient_sphere sphere_blue"></div>

    <!-- Header Stats Ribbon -->
    <header class="ribbon_bar">
      <div class="ribbon_left">
        <h3 class="ribbon_title">
          {{ sharedNotesSection ? 'Shared Notes' : 'Personal Notes' }}
        </h3>
        <span class="ribbon_pill">{{ totalCount }}</span>
      </div>
    </header>

    <!-- Scroll Feed Container -->
    <main class="bento_scroll_container">
      <!-- Loading Skeleton Grid -->
      <div v-if="isLoading" class="bento_grid">
        <!-- <div v-for="n in 1" :key="'skel-' + n" class="skeleton_tile">
          <div class="skel_icon"></div>
          <div class="skel_text"></div>
        </div> -->
      </div>

      <!-- Empty State -->
      <div v-else-if="isEmpty" class="empty_slate">
        <div class="empty_slate_icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <rect x="4" y="4" width="16" height="16" rx="3" stroke-width="1.6"/>
            <path d="M9 9h6M9 13h4" stroke-width="1.6" stroke-linecap="round"/>
          </svg>
        </div>
        <h4>{{ sharedNotesSection ? 'No shared notes' : 'Draft New Note' }}</h4>
        <p>{{ sharedNotesSection ? 'Incoming notes will appear here.' : 'Click the pen icon above to begin drafting a note.' }}</p>
      </div>

      <!-- Shared Notes Bento Grid -->
      <div v-else-if="sharedNotesSection" class="bento_grid">
        <div
          v-for="(entry, index) in allSharedNotesResults"
          :key="getSharedRowKey(entry)"
          class="bento_tile"
          role="button"
          tabindex="0"
          @click="$emit('select-shared-note', { entry, isCore: false })"
        >
          <!-- <div class="tile_accent_strip shared"></div> -->

          <div class="tile_core">
            <div class="tile_leading">
              <div class="tile_icon_frame shared">
                <i :class="entry.isGroup ? 'fa fa-users' : 'fa fa-file-text-o'"></i>
              </div>

              <div class="tile_title_stack">
                <div class="title_line">
                  <span class="tile_seq">#{{ index + 1 }}</span>
                  <span class="tile_heading" :title="extractSharedTitle(entry.item)">
                    {{ extractSharedTitle(entry.item) }}
                  </span>
                  <span v-if="entry.item.is_read === false" class="badge_unread">New</span>
                </div>

                <div class="tile_meta_pills">
                  <span class="meta_chip">
                    {{ extractSharedDate(entry.item) | dateformat }}
                  </span>
                  <span class="meta_chip author">
                    <svg viewBox="0 0 16 16" fill="currentColor">
                      <path d="M8 8a3 3 0 100-6 3 3 0 000 6zm2-3a2 2 0 11-4 0 2 2 0 014 0zm4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4zm-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10c-2.29 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664h10z" />
                    </svg>
                    {{ extractSharedAuthor(entry.item) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Personal Notes Bento Grid -->
      <div v-else class="bento_grid">
        <div
          v-for="note in visibleOwnNotes"
          :key="note.id"
          class="bento_tile personal"
          role="button"
          tabindex="0"
          @click="$emit('select-note', note.id)"
        >
          <!-- <div class="tile_accent_strip personal"></div> -->

          <div class="tile_core">
            <div class="tile_leading">
              <div class="tile_icon_frame personal">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </div>

              <div class="tile_title_stack">
                <div class="title_line">
                  <span class="tile_heading" :title="note.title || 'Untitled Note'">
                    {{ note.title || 'Untitled Note' }}
                  </span>
                </div>

                <div class="tile_meta_pills">
                  <span class="meta_chip">
                    <svg viewBox="0 0 16 16" fill="currentColor">
                      <path fill-rule="evenodd" d="M8 15A7 7 0 108 1a7 7 0 000 14zm.75-10.25a.75.75 0 00-1.5 0v4.25c0 .414.336.75.75.75h3.25a.75.75 0 000-1.5h-2.5V4.75z" clip-rule="evenodd" />
                    </svg>
                    {{ note.created_at | dateformat }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Floating Action -->
            <button
              type="button"
              class="tile_action_trigger"
              title="Share Note"
              @click.stop="$emit('share-note', note)"
            >
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path d="M13 4.5a2.5 2.5 0 11.702 1.737L6.97 9.604a2.518 2.518 0 010 .792l6.733 3.367a2.5 2.5 0 11-.671 1.341l-6.733-3.367a2.5 2.5 0 110-3.474l6.733-3.366A2.52 2.52 0 0113 4.5z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
export default {
  name: 'NotesListView',

  props: {
    sharedNotesSection: { type: Boolean, default: false },
    isLoadingNotes: { type: Boolean, default: false },
    isLoadingShared: { type: Boolean, default: false },
    visibleOwnNotes: { type: Array, default: () => [] },
    allSharedNotesResults: { type: Array, default: () => [] }
  },

  computed: {
    isLoading() {
      return this.sharedNotesSection ? this.isLoadingShared : this.isLoadingNotes;
    },

    totalCount() {
      return this.sharedNotesSection ? this.allSharedNotesResults.length : this.visibleOwnNotes.length;
    },

    isEmpty() {
      return this.totalCount === 0;
    }
  },

  methods: {
    getSharedRowKey(entry) {
      const item = entry && entry.item ? entry.item : {};
      return (entry.isGroup ? 'grp_' : 'usr_') + (item.id || (item.user_notes && item.user_notes.id));
    },

    extractSharedNote(item) {
      return (item && item.user_notes) || {};
    },

    extractSharedTitle(item) {
      return this.extractSharedNote(item).title || 'Untitled Note';
    },

    extractSharedDate(item) {
      return this.extractSharedNote(item).created_at || '';
    },

    extractSharedAuthor(item) {
      const u = this.extractSharedNote(item).user || {};
      return [u.first_name, u.last_name].filter(Boolean).join(' ') || 'Shared';
    }
  }
};
</script>

<style lang="scss" scoped>
$bg-slate: #0b0c10;
$tile-glass: rgba(22, 24, 33, 0.50);
$tile-hover: rgba(30, 33, 46, 0.6);
$glass-edge: rgba(255, 255, 255, 0.01);
$glass-top-edge: rgba(255, 255, 255, 0.03);

$text-bright: #ffffff;
$text-secondary: #94a3b8;
$text-muted: #5e6473;

$brand-indigo: #87cefa;
$brand-cyan: #87cefa;
$accent-unread: #f43f5e;

.bento_canvas {
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  background-color: $bg-slate;
  color: $text-bright;
  font-family: -apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", Roboto, sans-serif;
  overflow: hidden;
}

/* Ambient Spheres */
.ambient_sphere {
  position: absolute;
  border-radius: 50%;
  filter: blur(120px);
  pointer-events: none;
  opacity: 0.16;
  z-index: 0;

  &.sphere_purple {
    width: 320px;
    height: 320px;
    top: -50px;
    left: 10%;
    background: radial-gradient(circle, $brand-indigo, transparent 70%);
  }

  &.sphere_blue {
    width: 300px;
    height: 300px;
    bottom: -50px;
    right: 10%;
    background: radial-gradient(circle, $brand-cyan, transparent 70%);
  }
}

/* Top Sub-bar Ribbon */
.ribbon_bar {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  background: rgba(12, 13, 18, 0.6);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);

  .ribbon_left {
    display: flex;
    align-items: center;
    gap: 10px;

    .ribbon_title {
      margin: 0;
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.1em;
      color: $text-secondary;
    }

    .ribbon_pill {
      font-size: 11px;
      font-weight: 600;
      color: $text-secondary;
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.08);
      padding: 1px 8px;
      border-radius: 12px;
    }
  }
}

/* Scroll Container */
.bento_scroll_container {
  position: relative;
  z-index: 1;
  flex: 1;
  overflow-y: auto;
  padding: 14px 16px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.08);
    border-radius: 4px;
  }
}

/* Bento Grid System */
.bento_grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
}

/* Individual Bento Tile */
.bento_tile {
  position: relative;
  display: flex;
  background: $tile-glass;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid $glass-edge;
  border-top-color: $glass-top-edge;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  outline: none;
  transition: all 0.22s cubic-bezier(0.16, 1, 0.3, 1);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);

    .tile_action_trigger {
      opacity: 1;
      transform: scale(1);
    }
  }

  // Left Colored Accent Line
  .tile_accent_strip {
    width: 3px;
    flex-shrink: 0;

    &.shared {
      background: linear-gradient(180deg, #6366f1 0%, #a855f7 100%);
    }

    &.personal {
      background: linear-gradient(180deg, #06b6d4 0%, #3b82f6 100%);
    }
  }

  .tile_core {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 14px;
    gap: 12px;
    min-width: 0;
  }

  .tile_leading {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
    flex: 1;
  }

  .tile_icon_frame {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &.shared {
      background: rgba(99, 102, 241, 0.12);
      border: 1px solid rgba(99, 102, 241, 0.22);
      color: #818cf8;
    }

    &.personal {
      background: rgba(135, 206, 250, 0.1);
      border: 1px solid rgba(135, 206, 250, 0.2);
      color: #87cefa;
    }

    svg {
      width: 16px;
      height: 16px;
    }
  }

  .tile_title_stack {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
    flex: 1;

    .title_line {
      display: flex;
      align-items: center;
      gap: 7px;
      min-width: 0;

      .tile_seq {
        font-size: 11px;
        font-weight: 700;
        color: $text-muted;
      }

      .tile_heading {
        font-size: 13.5px;
        font-weight: 600;
        letter-spacing: -0.01em;
        color: #ffffff;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .badge_unread {
        font-size: 9px;
        font-weight: 700;
        text-transform: uppercase;
        padding: 2px 6px;
        border-radius: 4px;
        background: rgba(244, 63, 94, 0.15);
        border: 1px solid rgba(244, 63, 94, 0.3);
        color: $accent-unread;
      }
    }

    .tile_meta_pills {
      display: flex;
      align-items: center;
      gap: 8px;
      line-height: 1;
      margin-top: 2px;

      .meta_chip {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        font-size: 11px;
        font-weight: 500;
        color: $text-muted;
        line-height: 1;

        svg {
          width: 11px;
          height: 11px;
        }

        &.author {
          color: #f472b6;
        }
      }
    }
  }

  .tile_action_trigger {
    background: transparent;
    border: none;
    color: $text-muted;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    opacity: 0.35;
    transition: all 0.15s ease;
    flex-shrink: 0;

    svg {
      width: 14px;
      height: 14px;
    }

    &:hover {
      background: rgba(255, 255, 255, 0.08);
      color: #ffffff;
    }
  }
}

/* Skeleton Loading */
.skeleton_tile {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 10px;

  .skel_icon {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.05);
    animation: shimmer 1.5s infinite ease-in-out;
  }

  .skel_text {
    flex: 1;
    height: 12px;
    border-radius: 4px;
    background: rgba(255, 255, 255, 0.05);
    animation: shimmer 1.5s infinite ease-in-out;
  }
}

@keyframes shimmer {
  0% { opacity: 0.3; }
  50% { opacity: 0.7; }
  100% { opacity: 0.3; }
}

/* Empty State */
.empty_slate {
  margin: auto;
  text-align: center;
  padding: 40px 16px;

  .empty_slate_icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.07);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 14px auto;
    color: $text-muted;

    svg { width: 22px; height: 22px; }
  }

  h4 {
    margin: 0 0 6px 0;
    font-size: 16px;
    font-weight: 600;
    color: #ffffff;
  }

  p {
    margin: 0;
    font-size: 12px;
    line-height: 1.5;
    color: $text-muted;
    font-weight: 500;
  }
}
</style>